import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client/react'
import { format, formatISO } from 'date-fns'
import showDate from 'helpers/date/showDate'

import { AddIcon } from '@pleio/icons/add'
import { CrossIcon } from '@pleio/icons/cross'

import Button from 'js/components/Button'
import Flexer from 'js/components/Flexer/Flexer'
import IconButton from 'js/components/IconButton'
import TabMenu from 'js/components/Navigation/TabMenu'
import TabPanel from 'js/components/Navigation/TabPanel'
import Text from 'js/components/Text/Text'
import type { Flatten } from 'js/types/Flatten'

import type { EventItemViewFragment } from '../EventPage/EventItemView.fragment.generated'

import { Activity } from './Activity'
import { EDIT_SLOTS } from './SlotsForm.mutations'
import TabElement from './TabElement'

type ActivityType = Flatten<
    Omit<
        NonNullable<NonNullable<EventItemViewFragment['children']>[number]>,
        '__typename'
    > & {
        name?: string | null
    }
>

type SlotType = Flatten<
    Omit<
        NonNullable<
            NonNullable<EventItemViewFragment['slotsAvailable']>[number]
        >,
        '__typename' | 'name' | 'subEventGuids'
    > & {
        name: string
        // FIXME: subEventGuids: Array<string | null> | null
        // Ask backend to model their arrays, they should not pass us nullables inside an array
        // [Foo!] not [Foo] ... then we have to do lots of filtering
        subEventGuids?: string[]
    }
>

type Props = {
    entity: EventItemViewFragment
    onClose: () => void
}

const SlotsForm = ({ entity, onClose }: Props) => {
    const { t } = useTranslation()

    const [slotsAvailable, setSlotsAvailable] = useState<SlotType[]>(() =>
        (entity.slotsAvailable ?? [])
            .filter((slot) => slot !== null)
            .map((slot) => ({
                ...slot,
                name: slot.name ?? '',
                subEventGuids: (slot.subEventGuids ?? []).filter(
                    (guid) => guid !== null,
                ),
            })),
    )

    const [activeSlotIndex, setActiveSlotIndex] = useState(0)

    const [activeSlotActivities, setActiveSlotActivities] = useState<
        ActivityType[]
    >([])

    useEffect(() => {
        const activities: ActivityType[] = []

        entity.children?.forEach((activity) => {
            if (!activity) {
                return
            }
            const guids = slotsAvailable[activeSlotIndex]?.subEventGuids
            const name = slotsAvailable[activeSlotIndex]?.name

            if (guids && guids.indexOf(activity.guid) !== -1) {
                activities.push({ ...activity, name })
            }
        })

        setActiveSlotActivities(activities)
    }, [activeSlotIndex, slotsAvailable, entity.children])

    const addSlot = () => {
        const index = slotsAvailable.length
        setSlotsAvailable((prev) => [
            ...prev,
            {
                name: `Slot ${index + 1}`,
                subEventGuids: [],
            },
        ])
        setActiveSlotIndex(index)
    }

    const [editSlots] = useMutation(EDIT_SLOTS)

    const submit = async () => {
        await editSlots({
            variables: {
                input: {
                    guid: entity.guid,
                    slotsAvailable: slotsAvailable.map((el) => {
                        return {
                            name: el.name,
                            subEventGuids: el.subEventGuids,
                        }
                    }),
                },
            },
        })
            .then(() => {
                onClose()
            })
            .catch((errors) => {
                console.error(errors)
            })
    }

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = useForm()

    const [activeDate, setActiveDate] = useState<string | null>(null)

    const activities: Record<string, ActivityType[]>[] = []
    entity.children?.forEach((activity) => {
        if (!activity) {
            return
        }
        const key = format(new Date(activity.startDate ?? 0), 'yyyy-MM-dd')
        const dateIndex = activities.findIndex((el) => {
            return Object.keys(el)[0] === key
        })

        if (dateIndex === -1) {
            activities.push({ [key]: [activity] })
        } else {
            Object.values(activities[dateIndex])[0].push(activity)
        }
    })

    const activitiesByDate =
        activeDate &&
        activities.find((el) => {
            return Object.keys(el)[0] === activeDate
        })
    const currentActivities = activitiesByDate
        ? Object.values(activitiesByDate)[0]
        : (Object.values(activities[0] ?? {})[0] ?? [])

    return (
        <>
            <Text as="p" size="small">
                {t('entity-event.activity-slots-helper')}
            </Text>

            <div style={{ marginTop: '16px' }}>
                {activities.length > 1 && (
                    <TabMenu
                        label={t('entity-event.activities')}
                        showBorder
                        options={activities.map((activity, i) => {
                            const key = Object.keys(activity)[0]

                            const { guid } = activity[key][0]

                            return {
                                id: `tab-${guid}`,
                                label: showDate(formatISO(key), false, true),
                                onClick: () => setActiveDate(key),
                                isActive: activeDate
                                    ? activeDate === key
                                    : i === 0,
                                ariaControls: `tabpanel-${guid}`,
                            }
                        })}
                        style={{ margin: '8px 0 16px' }}
                    />
                )}

                <div>
                    {currentActivities.map((activity, i) => {
                        const { guid, title, rsvp, attendEventWithoutAccount } =
                            activity

                        const addActivity = () => {
                            if (slotsAvailable.length > 0) {
                                setSlotsAvailable((prev) =>
                                    prev.map((slot, idx) =>
                                        idx === activeSlotIndex
                                            ? {
                                                  ...slot,
                                                  subEventGuids: [
                                                      ...slot.subEventGuids,
                                                      activity.guid,
                                                  ],
                                              }
                                            : slot,
                                    ),
                                )
                            } else {
                                setSlotsAvailable([
                                    {
                                        name: 'Slot 1',
                                        subEventGuids: [activity.guid],
                                    },
                                ])
                            }
                        }

                        return (
                            <TabPanel
                                key={guid}
                                id={`tabpanel-${guid}`}
                                aria-labelledby={`tab-${guid}`}
                                visible
                            >
                                <Activity
                                    entity={activity}
                                    isFirst={i === 0}
                                    isLast={i === currentActivities.length - 1}
                                >
                                    {activeSlotActivities.findIndex(
                                        (el) => el.guid === guid,
                                    ) === -1 &&
                                        (rsvp || attendEventWithoutAccount) && (
                                            <IconButton
                                                size="large"
                                                bgOffset="sm"
                                                variant="secondary"
                                                onClick={addActivity}
                                                tooltip={
                                                    slotsAvailable.length > 0
                                                        ? t(
                                                              'entity-event.add-to-slot',
                                                          )
                                                        : t(
                                                              'entity-event.add-to-new-slot',
                                                          )
                                                }
                                                tooltipProps={{ side: 'right' }}
                                                aria-label={
                                                    slotsAvailable.length > 0
                                                        ? t(
                                                              'entity-event.add-to-slot-label',
                                                              {
                                                                  title,
                                                                  name: slotsAvailable[
                                                                      activeSlotIndex
                                                                  ].name,
                                                              },
                                                          )
                                                        : t(
                                                              'entity-event.add-to-new-slot-label',
                                                              { title },
                                                          )
                                                }
                                            >
                                                <AddIcon />
                                            </IconButton>
                                        )}
                                </Activity>
                            </TabPanel>
                        )
                    })}
                </div>
            </div>

            {slotsAvailable.length > 0 ? (
                <TabMenu
                    label={t('entity-event.activity-slots')}
                    edgePadding
                    edgeMargin
                    showBorder
                    options={slotsAvailable.map(({ name }, i) => {
                        return {
                            name,
                            label: name,
                            onClick: () => setActiveSlotIndex(i),
                            isActive: activeSlotIndex === i,
                            slotIndex: i,
                            id: `tab-${name}`,
                            'aria-controls': `tabpanel-${name}`,
                        }
                    })}
                    // @ts-ignore
                    TabElement={(props: any) => (
                        <TabElement
                            {...props}
                            setSlotsAvailable={setSlotsAvailable}
                            setActiveSlotIndex={setActiveSlotIndex}
                        />
                    )}
                    style={{ marginTop: '16px' }}
                >
                    <Flexer divider dividerOffset="sm" gutter={false}>
                        <div
                            style={{
                                alignSelf: 'stretch',
                            }}
                        />
                        <IconButton
                            size="normal"
                            variant="secondary"
                            onClick={addSlot}
                            aria-label={t('entity-event.add-slot')}
                        >
                            <AddIcon />
                        </IconButton>
                    </Flexer>
                </TabMenu>
            ) : (
                <div style={{ textAlign: 'center', padding: '20px 20px 0' }}>
                    {t('entity-event.no-slots')}{' '}
                    <Button
                        size="small"
                        variant="tertiary"
                        onClick={() => {
                            setSlotsAvailable([
                                {
                                    name: 'Slot 1',
                                    subEventGuids: [],
                                },
                            ])
                        }}
                        style={{ display: 'inline-flex' }}
                    >
                        {t('entity-event.add-slot')}
                    </Button>
                </div>
            )}

            {activeSlotActivities?.length > 0 && (
                <TabPanel
                    id={`tabpanel-${activeSlotActivities[0]?.name}`}
                    aria-labelledby={`tab-${activeSlotActivities[0]?.name}`}
                    visible
                >
                    <div style={{ marginTop: '16px' }}>
                        {activeSlotActivities.map((activity, i) => {
                            const { guid, title } = activity
                            const removeActivity = () => {
                                setSlotsAvailable((prev) =>
                                    prev.map((slot, idx) =>
                                        idx === activeSlotIndex
                                            ? {
                                                  ...slot,
                                                  subEventGuids:
                                                      slot.subEventGuids.filter(
                                                          (guid) =>
                                                              guid !==
                                                              activity.guid,
                                                      ),
                                              }
                                            : slot,
                                    ),
                                )
                            }

                            return (
                                <Activity
                                    key={guid}
                                    entity={activity}
                                    isFirst={i === 0}
                                    isLast={
                                        i === activeSlotActivities.length - 1
                                    }
                                >
                                    <IconButton
                                        size="large"
                                        bgOffset="sm"
                                        variant="secondary"
                                        onClick={removeActivity}
                                        tooltip={t(
                                            'entity-event.remove-from-slot',
                                        )}
                                        aria-label={t(
                                            'entity-event.remove-from-slot-label',
                                            { title },
                                        )}
                                        tooltipProps={{ side: 'right' }}
                                    >
                                        <CrossIcon />
                                    </IconButton>
                                </Activity>
                            )
                        })}
                    </div>
                </TabPanel>
            )}

            <Flexer mt as="form" onSubmit={handleSubmit(submit)}>
                <Button
                    size="normal"
                    variant="tertiary"
                    type="button"
                    onClick={onClose}
                >
                    {t('action.cancel')}
                </Button>
                <Button
                    type="submit"
                    size="normal"
                    variant="primary"
                    loading={isSubmitting}
                >
                    {t('action.save')}
                </Button>
            </Flexer>
        </>
    )
}

export default SlotsForm
